import { useCallback, useEffect, useMemo, useState } from "react";
import {
  TrendingUp,
  TrendingDown,
  Clock,
  DollarSign,
  FileText,
  Calendar,
} from "lucide-react";
import { getAuth } from "firebase/auth";

/**
 * FinanceOverview (render-only)
 * - Backend does ALL calculations
 * - FE only fetches + displays
 *
 * Endpoint:
 * GET /api/invoice-tracker/overview
 */

const API_BASE = ""; // same origin (proxy -> backend)
const API_URL = "/api/invoice-tracker/overview";

/* ===========================
 * Helpers
 * =========================== */
function safeNum(n, fb = 0) {
  const x = Number(n);
  return Number.isFinite(x) ? x : fb;
}

function toIdr(n) {
  const v = safeNum(n, 0);
  try {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(v);
  } catch {
    return `Rp ${Math.round(v).toLocaleString("id-ID")}`;
  }
}

/**
 * Compact Indonesian formatting:
 * - >= 1B   -> "Rp 1,2 M"
 * - >= 1M   -> "Rp 10,9 jt"
 * - >= 1K   -> "Rp 545 rb"
 * - else    -> "Rp 500"
 *
 * Tooltip can show full value.
 */
function toIdrShort(n) {
  const v = safeNum(n, 0);
  const abs = Math.abs(v);

  if (abs < 1000) return `Rp ${Math.round(v).toLocaleString("id-ID")}`;

  const fmt1 = (x) =>
    new Intl.NumberFormat("id-ID", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    }).format(x);

  const fmt0 = (x) =>
    new Intl.NumberFormat("id-ID", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(x);

  if (abs >= 1_000_000_000) return `Rp ${fmt1(v / 1_000_000_000)} M`;
  if (abs >= 1_000_000) return `Rp ${fmt1(v / 1_000_000)} jt`;
  if (abs >= 1_000) return `Rp ${fmt0(v / 1_000)} rb`;
  return `Rp ${fmt0(v)}`;
}

function fmtPct(p) {
  const x = Number(p);
  if (!Number.isFinite(x)) return "+0%";
  const sign = x >= 0 ? "+" : "";
  return `${sign}${Math.round(x)}%`;
}

function cn(...xs) {
  return xs.filter(Boolean).join(" ");
}

/* ===========================
 * UI atoms
 * =========================== */
function StatChip({ label, value, tone = "gray" }) {
  const toneMap = {
    gray: "bg-gray-100 text-gray-700 ring-gray-200",
    orange: "bg-orange-50 text-orange-700 ring-orange-200",
    blue: "bg-blue-50 text-blue-700 ring-blue-200",
    red: "bg-red-50 text-red-700 ring-red-200",
    green: "bg-green-50 text-green-700 ring-green-200",
    purple: "bg-purple-50 text-purple-700 ring-purple-200",
  };

  return (
    <div
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] ring-1 whitespace-nowrap",
        toneMap[tone] || toneMap.gray
      )}
    >
      <span className="font-semibold tabular-nums">{value}</span>
      <span>{label}</span>
    </div>
  );
}

function CardShell({
  title,
  icon,
  iconWrapClassName,
  main,
  subline,
  footer,
  mainTitle, // tooltip
}) {
  return (
    <div className="group bg-white rounded-2xl border border-gray-200 p-5 sm:p-6 shadow-sm hover:shadow-md transition-shadow min-w-0">
      <div className="flex items-start gap-4">
        <div
          className={cn(
            "w-11 h-11 sm:w-12 sm:h-12 rounded-2xl flex items-center justify-center shrink-0 ring-1 ring-black/5",
            iconWrapClassName
          )}
        >
          {icon}
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-700 truncate">{title}</p>

          <p
            title={mainTitle || (typeof main === "string" ? main : undefined)}
            className={cn(
              "mt-1 font-extrabold text-gray-900 tabular-nums",
              "whitespace-nowrap overflow-hidden text-ellipsis",
              "text-xl sm:text-2xl leading-[1.15]"
            )}
          >
            {main}
          </p>

          {subline ? (
            <div className="mt-1 text-xs text-gray-500">{subline}</div>
          ) : null}
        </div>
      </div>

      {footer ? (
        <div className="mt-4 pt-4 border-t border-gray-100">{footer}</div>
      ) : null}
    </div>
  );
}

/* ===========================
 * Data defaults
 * =========================== */
const ZERO_DATA = {
  updatedAt: null,
  cards: {
    piutang_usaha: {
      value: 0,
      unpaid_count: 0,
      overdue_count: 0,
      paid_count: 0, // FE expects this (backend update later)
    },
    pendapatan_bulan_ini: {
      value: 0,
      mom_pct: 0,
    },
    total_invoice: {
      total_count: 0,
      lunas_count: 0,
      unpaid_count: 0,
      void_count: 0,
    },
    waktu_pembayaran: {
      avg_days: 0,
    },
  },
};

/* ===========================
 * Auth helpers
 * =========================== */
async function getFirebaseIdToken() {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) return null;
  return await user.getIdToken(false);
}

async function apiFetch(path, options = {}) {
  const token = await getFirebaseIdToken();
  if (!token) throw new Error("No token provided (user not logged in)");

  const res = await fetch(`${API_BASE}${path}`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
    credentials: "include",
    ...options,
  });

  const contentType = res.headers.get("content-type") || "";
  const isJson = contentType.includes("application/json");
  const data = isJson
    ? await res.json().catch(() => null)
    : await res.text().catch(() => null);

  if (!res.ok) {
    const message =
      (data && typeof data === "object" && (data.message || data.error)) ||
      (typeof data === "string" && data) ||
      `Request failed (${res.status})`;
    throw new Error(message);
  }

  return data;
}

/* ===========================
 * Component
 * =========================== */
export default function FinanceOverview({ isActive }) {
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [data, setData] = useState(ZERO_DATA);

  const loadOverview = useCallback(async () => {
    setLoading(true);
    setErrorMsg("");

    try {
      const json = await apiFetch(API_URL, { method: "GET" });

      setData({
        updatedAt: json?.updatedAt || new Date().toISOString(),
        cards: {
          piutang_usaha: {
            ...ZERO_DATA.cards.piutang_usaha,
            ...(json?.cards?.piutang_usaha || {}),
          },
          pendapatan_bulan_ini: {
            ...ZERO_DATA.cards.pendapatan_bulan_ini,
            ...(json?.cards?.pendapatan_bulan_ini || {}),
          },
          total_invoice: {
            ...ZERO_DATA.cards.total_invoice,
            ...(json?.cards?.total_invoice || {}),
          },
          waktu_pembayaran: {
            ...ZERO_DATA.cards.waktu_pembayaran,
            ...(json?.cards?.waktu_pembayaran || {}),
          },
        },
      });
    } catch (err) {
      setErrorMsg(String(err?.message || "Gagal memuat ringkasan"));
      setData({ ...ZERO_DATA, updatedAt: new Date().toISOString() });
    } finally {
      setLoading(false);
    }
  }, []);

  // ✅ Fetch on mount (optional but nice if overview is default)
  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  // ✅ Fetch every time the tab becomes active
  useEffect(() => {
    if (!isActive) return;
    loadOverview();
  }, [isActive, loadOverview]);

  const updatedLabel = useMemo(() => {
    const dt = data?.updatedAt ? new Date(data.updatedAt) : new Date();
    return dt.toLocaleString("id-ID", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }, [data?.updatedAt]);

  const c = data.cards;

  const momPct = safeNum(c?.pendapatan_bulan_ini?.mom_pct, 0);
  const momPositive = momPct >= 0;

  const piutangFull = toIdr(c.piutang_usaha.value);
  const pendapatanFull = toIdr(c.pendapatan_bulan_ini.value);

  const piutangMain = loading ? "—" : toIdrShort(c.piutang_usaha.value);
  const pendapatanMain = loading ? "—" : toIdrShort(c.pendapatan_bulan_ini.value);

  const paidCount = safeNum(c?.piutang_usaha?.paid_count, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <h2 className="text-2xl font-bold text-gray-900">Ringkasan Keuangan</h2>
          <p className="text-gray-600 mt-1">Ringkasan keuangan berdasarkan Invoice</p>
        </div>

        <div className="text-sm text-gray-500 shrink-0">
          Terakhir diperbarui: <span className="font-medium">{updatedLabel}</span>
        </div>
      </div>

      {/* Error (non-blocking) */}
      {errorMsg ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <div className="font-semibold">Gagal memuat ringkasan</div>
          <div className="mt-1">{errorMsg}</div>
          <div className="text-red-600/80 mt-2">(Menampilkan nilai default 0)</div>
        </div>
      ) : null}

      {/* Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-6">
        {/* Piutang Usaha */}
        <CardShell
          title="Piutang Usaha"
          iconWrapClassName="bg-blue-50"
          icon={<TrendingUp className="w-6 h-6 text-blue-600" />}
          main={piutangMain}
          mainTitle={loading ? "" : piutangFull}
          subline="Total tagihan aktif (non-void/non-cancelled)"
          footer={
            <div className="flex flex-wrap gap-2">
              <StatChip tone="green" label="Paid" value={loading ? "—" : paidCount} />
              <StatChip
                tone="orange"
                label="Unpaid"
                value={loading ? "—" : c.piutang_usaha.unpaid_count}
              />
              <StatChip
                tone="red"
                label="Overdue"
                value={loading ? "—" : c.piutang_usaha.overdue_count}
              />
            </div>
          }
        />

        {/* Pendapatan Bulan Ini */}
        <CardShell
          title="Pendapatan Bulan Ini"
          iconWrapClassName="bg-green-50"
          icon={<DollarSign className="w-6 h-6 text-green-600" />}
          main={pendapatanMain}
          mainTitle={loading ? "" : pendapatanFull}
          subline="Total invoice paid bulan berjalan"
          footer={
            <div className="flex items-center justify-between gap-3">
              <div
                className={cn(
                  "inline-flex items-center gap-2 text-xs font-medium",
                  momPositive ? "text-green-700" : "text-red-700"
                )}
              >
                {momPositive ? (
                  <TrendingUp className="w-4 h-4" />
                ) : (
                  <TrendingDown className="w-4 h-4" />
                )}
                <span>{loading ? "—" : `${fmtPct(momPct)} dibanding bulan lalu`}</span>
              </div>

              {loading ? (
                <span className="inline-flex items-center gap-2 text-xs text-gray-400">
                  <span className="w-2 h-2 rounded-full bg-gray-300 animate-pulse" />
                  loading
                </span>
              ) : null}
            </div>
          }
        />

        {/* Total Invoice */}
        <CardShell
          title="Total Invoice"
          iconWrapClassName="bg-orange-50"
          icon={<FileText className="w-6 h-6 text-orange-600" />}
          main={loading ? "—" : String(c.total_invoice.total_count)}
          subline="Jumlah invoice berdasarkan status"
          footer={
            <div className="flex flex-wrap gap-2">
              <StatChip tone="green" label="Lunas" value={loading ? "—" : c.total_invoice.lunas_count} />
              <StatChip
                tone="orange"
                label="Unpaid"
                value={loading ? "—" : c.total_invoice.unpaid_count}
              />
              <StatChip tone="red" label="Void" value={loading ? "—" : c.total_invoice.void_count} />
            </div>
          }
        />

        {/* Waktu Pembayaran */}
        <CardShell
          title="Waktu Pembayaran"
          iconWrapClassName="bg-purple-50"
          icon={<Calendar className="w-6 h-6 text-purple-600" />}
          main={loading ? "—" : `${Math.round(safeNum(c.waktu_pembayaran.avg_days, 0))} hari`}
          subline="Rata-rata waktu pelunasan"
          footer={
            <div className="flex items-center gap-2 text-xs text-purple-700 font-medium">
              <Clock className="w-4 h-4" />
              <span>Rata-rata dari invoice lunas</span>
            </div>
          }
        />
      </div>

      {/* Welcome banner */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl p-6 sm:p-8 text-white shadow-sm">
        <h3 className="text-2xl font-bold mb-3">Selamat Datang di Sistem Invoice Tracker</h3>
        <p className="text-blue-50 leading-relaxed">
          Kelola seluruh invoice perusahaan perusahaan dalam satu platform. Gunakan menu navigasi di atas untuk
          mengakses berbagai fitur pengelolahan invoice penjualan dan riwayat transaksi.
        </p>
      </div>
    </div>
  );
}
